import { Button, Checkbox } from 'components/core';
import { FC, useState } from 'react';
import {
  Styled,
  OrderConfirmationProps,
  isCartContainsSubscriptionProducts,
} from '.';
import { useI18n } from 'contexts/i18n';
import {
  useContentful,
  useDoctorProfile,
  useRetriveCart,
} from 'reactQuery/queries';
import {
  isCartContainsCustomizedItems,
  isCartContainsItemsWithExpiration,
} from './';
import { useReadOnlyUser } from 'utils/hooks';

export const OrderTermsConditions: FC<OrderConfirmationProps> = ({
  onPlaceOrder,
  isLoading,
  isAcceptedBrandGuildlines,
  isAcceptedUsageGuildlines,
  setIsAcceptedBrandGuildlines,
  setIsAcceptedUsageGuildlines,
}) => {
  const i18n = useI18n();

  const { data: cart } = useRetriveCart();

  const isCartHavingCustomizedItems = isCartContainsCustomizedItems(
    cart?.lineItems || []
  );
  const isCartHavingItemsWithExpiration = isCartContainsItemsWithExpiration(
    cart?.lineItems || []
  );

  const isCartHavingSubscriptionProducts = isCartContainsSubscriptionProducts(
    cart?.lineItems || []
  );
  const { data: contentfulData } = useContentful();

  const { isReadOnlyUser } = useReadOnlyUser();
  const { privacyPolicy, termsOfSale } = contentfulData?.globalContents || {};
  const { data: doctorProfile } = useDoctorProfile();
  const { brandGuidelines } = contentfulData?.globalContents || {};
  const brandGuidelineLink = doctorProfile
    ? brandGuidelines[doctorProfile?.locale]
    : brandGuidelines['en-US'];
  const doctorCountry = doctorProfile?.country ?? 'US';

  let privacyPolicyLink = '';
  let termsOfSaleLink = '';

  if (doctorProfile && typeof doctorProfile.region !== 'undefined') {
    privacyPolicyLink =
      privacyPolicy[doctorCountry] ?? privacyPolicy[doctorProfile.region];
    termsOfSaleLink =
      termsOfSale[doctorCountry] ?? termsOfSale[doctorProfile.region];
  }

  const message = i18n['Place Order Review Message'];
  const linkMap: Record<string, { url: string; label: string }> = {
    TermsOfSale: { url: termsOfSaleLink, label: i18n['Terms Of Sale'] },
    PrivacyPolicy: { url: privacyPolicyLink, label: i18n['Privacy Policy'] },
  };
  const placeOrderReviewMessage = message
    .split(/(\{[^}]+\})/g)
    .map((part: string, index: number) => {
      if (part.startsWith('{') && part.endsWith('}')) {
        const key = part.slice(1, -1);
        const link = linkMap[key];
        if (link?.url) {
          return (
            <a
              href={link.url}
              key={index}
              target="_blank"
              rel="noopener noreferrer"
            >
              {link.label}
            </a>
          );
        }
      }
      return part;
    });

  return (
    <Styled.OrderReviewContainer>
      <Styled.TermsConditionsContainer>
        <Styled.TCList>
        <li>{placeOrderReviewMessage}</li>
          {isCartHavingSubscriptionProducts && (
            <li>{i18n['Place Order Subscription Review Message']}</li>
          )}
          {isCartHavingItemsWithExpiration && (
            <li>
              <Checkbox
                label="I acknowledge I will stop using the marketing assets by the expiration dates indicated on their product pages."
                checked={isAcceptedUsageGuildlines}
                onChange={() => {
                  setIsAcceptedUsageGuildlines(!isAcceptedUsageGuildlines);
                }}
                marginLeft="0px"
              ></Checkbox>
              <Styled.TermsText></Styled.TermsText>
            </li>
          )}
        </Styled.TCList>
      </Styled.TermsConditionsContainer>

      <Styled.ButtonWrapper>
        <Button
          size="lg"
          fullWidth={true}
          onClick={onPlaceOrder}
          disabled={
            isLoading ||
            isReadOnlyUser ||
            (isCartHavingCustomizedItems && !isAcceptedBrandGuildlines) ||
            (isCartHavingItemsWithExpiration && !isAcceptedUsageGuildlines)
          }
        >
          {i18n['Place Order']}
        </Button>
      </Styled.ButtonWrapper>
    </Styled.OrderReviewContainer>
  );
};
